import {
  DimensionFilterConstraint,
  MetricWithDimensionFields,
  PopulationDerivedFilter,
  PrepopulateWarning,
} from '../../constants/analysis';
import { PrepopulateFiltersResponse } from './controlled-metrics/types';
import Service from '@/lib/App';
import { Constraint } from '@/lib/PlannerDTOs';
import * as logger from '@/utils/logging';

// Status types for population filter application
export type PopulationFilterStatus = 'success' | 'partial' | 'failed' | 'none';

// Result type for metric processing
export interface ProcessMetricResult {
  metric: MetricWithDimensionFields;
  changed: boolean;
}

/**
 * Get display label for source facet
 */
export const getSourceFacetLabel = (sourceFacet: string): string => {
  const labels: Record<string, string> = {
    city: 'City',
    ctry: 'Country',
    svc: 'Taxi type',
  };
  return labels[sourceFacet] || sourceFacet;
};

/**
 * Get warning message based on reason
 */
export const getWarningReason = (warning: PrepopulateWarning): string => {
  switch (warning.reason) {
    case 'dimension_not_found':
      return 'dimension not supported';
    case 'country_code_not_found':
      return 'could not be resolved';
    case 'dimension_values_not_present':
      return 'values not found';
    case 'dimension_value_type_mismatch':
      return 'type mismatch';
    default:
      return 'failed to apply';
  }
};

/**
 * Determine the overall status of population filters for a metric
 */
export const getPopulationFilterStatus = (
  metric: MetricWithDimensionFields,
): PopulationFilterStatus => {
  const appliedFilters = metric.populationDerivedFilters || [];
  const warnings = metric.prepopulationWarnings || [];

  if (appliedFilters.length === 0 && warnings.length === 0) {
    return 'none';
  }

  if (warnings.length === 0 && appliedFilters.length > 0) {
    return 'success';
  }

  if (appliedFilters.length === 0 && warnings.length > 0) {
    return 'failed';
  }

  return 'partial';
};

/**
 * Check if a metric needs population filters applied
 * Returns true if the metric has a name but hasn't been processed yet
 */
export const needsPopulationFilters = (
  metric: MetricWithDimensionFields,
): boolean => {
  return (
    !!metric.name &&
    metric.populationDerivedFilters === undefined &&
    metric.prepopulationWarnings === undefined
  );
};

/**
 * Check if a metric was previously processed for population filters
 * Returns true if the metric has either populationDerivedFilters or prepopulationWarnings defined
 */
export const wasPopulationFiltersProcessed = (
  metric: MetricWithDimensionFields,
): boolean => {
  return (
    metric.populationDerivedFilters !== undefined ||
    metric.prepopulationWarnings !== undefined
  );
};

/**
 * Remove previously auto-applied population filters from a metric
 * This is used when constraints change and filters need to be re-applied
 */
export const cleanMetricForRepopulation = (
  metric: MetricWithDimensionFields,
): MetricWithDimensionFields => {
  const existingConstraints =
    metric.dimension_filters?.[0]?.dimension_filter_constraints || [];

  // Filter out constraints that were auto-applied from population
  const nonPopulationConstraints = existingConstraints.filter(
    (c) =>
      !metric.populationDerivedFilters?.some(
        (pf) => pf.dimension_name === c.dimension_name,
      ),
  );

  return {
    ...metric,
    dimension_filters:
      nonPopulationConstraints.length > 0
        ? [{ dimension_filter_constraints: nonPopulationConstraints }]
        : [{ dimension_filter_constraints: [] }],
    populationDerivedFilters: undefined,
    prepopulationWarnings: undefined,
  };
};

/**
 * Merge prepopulated constraints with existing constraints
 * Only adds constraints that don't already exist (by dimension_name)
 */
export const mergeConstraints = (
  existingConstraints: DimensionFilterConstraint[],
  prepopulatedConstraints: Array<{
    dimension_name: string;
    operator: string;
    type: string;
    values: (string | number | boolean)[];
  }>,
): DimensionFilterConstraint[] => {
  const mergedConstraints = [...existingConstraints];

  prepopulatedConstraints.forEach((prepopConstraint) => {
    const exists = existingConstraints.some(
      (ec) => ec.dimension_name === prepopConstraint.dimension_name,
    );
    if (!exists) {
      mergedConstraints.push({
        dimension_name: prepopConstraint.dimension_name,
        operator: prepopConstraint.operator,
        type: prepopConstraint.type as 'STRING' | 'INTEGER' | 'BOOLEAN',
        values: prepopConstraint.values,
      });
    }
  });

  return mergedConstraints;
};

/**
 * Extract population derived filter metadata from prepopulate result
 * Note: Go backend may omit `editable` field when false (omitempty),
 * so we default to false when not provided
 */
export const extractPopulationDerivedFilters = (
  dimensionFilterConstraints: Array<{
    dimension_name: string;
    source_facet: string;
    editable?: boolean;
  }>,
): PopulationDerivedFilter[] => {
  return dimensionFilterConstraints.map((constraint) => ({
    dimension_name: constraint.dimension_name,
    source_facet: constraint.source_facet,
    // Default to false when editable is omitted (Go omitempty behavior)
    editable: constraint.editable ?? false,
  }));
};

/**
 * Check if a dimension filter constraint is non-editable based on population derived filters
 */
export const isFilterNonEditable = (
  dimensionName: string | null,
  populationDerivedFilters?: PopulationDerivedFilter[],
): boolean => {
  if (!dimensionName || !populationDerivedFilters?.length) {
    return false;
  }

  const populationFilter = populationDerivedFilters.find(
    (pf) => pf.dimension_name === dimensionName,
  );

  // If this filter came from population and is not editable, it's non-editable
  return populationFilter !== undefined && populationFilter.editable === false;
};

/**
 * Apply population filters to a single metric by calling the prepopulate API
 */
export const applyPopulationFiltersToMetric = async (
  metric: MetricWithDimensionFields,
  constraints: Constraint[],
): Promise<MetricWithDimensionFields> => {
  if (!metric.name || !constraints.length) return metric;

  try {
    const response: PrepopulateFiltersResponse =
      await Service.planner.prepopulateDimensionFilters(
        [metric.name],
        constraints,
      );

    const result = (response?.results ?? []).find(
      (r) => r.metric === metric.name,
    );
    if (!result) return metric;

    // Extract population derived filter metadata
    const populationDerivedFilters = extractPopulationDerivedFilters(
      result.dimension_filters?.[0]?.dimension_filter_constraints || [],
    );

    // Merge prepopulated filters with existing filters
    const existingConstraints =
      metric.dimension_filters?.[0]?.dimension_filter_constraints || [];
    const prepopulatedConstraints =
      result.dimension_filters?.[0]?.dimension_filter_constraints || [];

    const mergedConstraints = mergeConstraints(
      existingConstraints,
      prepopulatedConstraints,
    );

    return {
      ...metric,
      dimension_filters:
        mergedConstraints.length > 0
          ? [{ dimension_filter_constraints: mergedConstraints }]
          : metric.dimension_filters,
      populationDerivedFilters,
      prepopulationWarnings: result.warnings as PrepopulateWarning[],
    };
  } catch (error) {
    logger.error(error, 'Failed to prepopulate dimension filters');
    return metric;
  }
};

/**
 * Process a single metric for population filter application
 * Handles both new metrics and re-application when constraints change
 *
 * @param metric - The metric to process
 * @param constraints - Current experiment constraints
 * @param constraintsChanged - Whether constraints have changed since last processing
 * @returns ProcessMetricResult with the updated metric and whether changes were made
 */
export const processMetricForPopulationFilters = async (
  metric: MetricWithDimensionFields,
  constraints: Constraint[],
  constraintsChanged: boolean,
): Promise<ProcessMetricResult> => {
  if (!metric.name) {
    return { metric, changed: false };
  }

  // If constraints changed, re-apply filters to metrics that were previously processed
  if (constraintsChanged && wasPopulationFiltersProcessed(metric)) {
    const cleanedMetric = cleanMetricForRepopulation(metric);
    const updated = await applyPopulationFiltersToMetric(
      cleanedMetric,
      constraints,
    );
    return { metric: updated, changed: true };
  }

  // Apply filters to new metrics that don't have them yet
  if (needsPopulationFilters(metric)) {
    const updated = await applyPopulationFiltersToMetric(metric, constraints);
    if (
      updated.populationDerivedFilters?.length ||
      updated.prepopulationWarnings?.length
    ) {
      return { metric: updated, changed: true };
    }
  }

  return { metric, changed: false };
};

/**
 * Process multiple metrics for population filter application using batch API
 *
 * @param metrics - Array of metrics to process
 * @param constraints - Current experiment constraints
 * @param constraintsChanged - Whether constraints have changed since last processing
 * @returns Array of ProcessMetricResult
 */
export const processMetricsForPopulationFilters = async (
  metrics: MetricWithDimensionFields[],
  constraints: Constraint[],
  constraintsChanged: boolean,
): Promise<ProcessMetricResult[]> => {
  if (!constraints.length) {
    return metrics.map((metric) => ({ metric, changed: false }));
  }

  // Prepare metrics for batch processing
  const metricsToProcess: Array<{
    index: number;
    metric: MetricWithDimensionFields;
    needsRepopulation: boolean;
  }> = [];

  const results: ProcessMetricResult[] = metrics.map((metric, index) => {
    if (!metric.name) {
      return { metric, changed: false };
    }

    // Check if metric needs repopulation due to constraint changes
    if (constraintsChanged && wasPopulationFiltersProcessed(metric)) {
      metricsToProcess.push({
        index,
        metric: cleanMetricForRepopulation(metric),
        needsRepopulation: true,
      });
      return { metric, changed: false }; // Placeholder, will be updated
    }

    // Check if metric needs initial population
    if (needsPopulationFilters(metric)) {
      metricsToProcess.push({
        index,
        metric,
        needsRepopulation: false,
      });
      return { metric, changed: false }; // Placeholder, will be updated
    }

    return { metric, changed: false };
  });

  // If no metrics need processing, return early
  if (metricsToProcess.length === 0) {
    return results;
  }

  // Make single batch API call for all metrics that need processing
  try {
    const metricNames = metricsToProcess.map((m) => m.metric.name as string);
    const response: PrepopulateFiltersResponse =
      await Service.planner.prepopulateDimensionFilters(
        metricNames,
        constraints,
      );

    // Map batch results back to individual metrics
    for (const item of metricsToProcess) {
      const result = (response?.results ?? []).find(
        (r) => r.metric === item.metric.name,
      );

      if (!result) {
        results[item.index] = {
          metric: item.needsRepopulation
            ? {
                ...item.metric,
                populationDerivedFilters: [],
                prepopulationWarnings: [],
              }
            : item.metric,
          changed: item.needsRepopulation,
        };
        continue;
      }

      // Extract population derived filter metadata
      const populationDerivedFilters = extractPopulationDerivedFilters(
        result.dimension_filters?.[0]?.dimension_filter_constraints || [],
      );

      // Merge prepopulated filters with existing filters
      const existingConstraints =
        item.metric.dimension_filters?.[0]?.dimension_filter_constraints || [];
      const prepopulatedConstraints =
        result.dimension_filters?.[0]?.dimension_filter_constraints || [];

      const mergedConstraints = mergeConstraints(
        existingConstraints,
        prepopulatedConstraints,
      );

      const updatedMetric: MetricWithDimensionFields = {
        ...item.metric,
        dimension_filters:
          mergedConstraints.length > 0
            ? [{ dimension_filter_constraints: mergedConstraints }]
            : item.metric.dimension_filters,
        populationDerivedFilters,
        prepopulationWarnings: result.warnings as PrepopulateWarning[],
      };

      const hasChanges =
        populationDerivedFilters.length > 0 ||
        (result.warnings?.length ?? 0) > 0 ||
        item.needsRepopulation;

      results[item.index] = {
        metric: updatedMetric,
        changed: hasChanges,
      };
    }
  } catch (error) {
    logger.error(error, 'Failed to batch prepopulate dimension filters');
    // On error, mark repopulated metrics as processed but with empty results
    for (const item of metricsToProcess) {
      if (item.needsRepopulation) {
        results[item.index] = {
          metric: {
            ...item.metric,
            populationDerivedFilters: [],
            prepopulationWarnings: [],
          },
          changed: true,
        };
      }
    }
  }

  return results;
};

/**
 * Check if any metrics in the results were changed
 */
export const hasMetricChanges = (results: ProcessMetricResult[]): boolean => {
  return results.some((r) => r.changed);
};

/**
 * Extract metrics from processing results
 */
export const extractMetricsFromResults = (
  results: ProcessMetricResult[],
): MetricWithDimensionFields[] => {
  return results.map((r) => r.metric);
};

/**
 * Result type for batch processing all metric types
 */
export interface BatchProcessAllMetricsResult {
  primary_metrics: MetricWithDimensionFields[];
  secondary_metrics: MetricWithDimensionFields[];
  guardrail_metrics: MetricWithDimensionFields[];
  control_metrics: MetricWithDimensionFields[];
  anyChanges: boolean;
}

/**
 * Process all metric types with a single batch API call
 * This is more efficient than processing each metric type separately
 */
export const processAllMetricTypesForPopulationFilters = async (
  metricsConfig: {
    primary_metrics: MetricWithDimensionFields[];
    secondary_metrics: MetricWithDimensionFields[];
    guardrail_metrics: MetricWithDimensionFields[];
    control_metrics: MetricWithDimensionFields[];
  },
  constraints: Constraint[],
  constraintsChanged: boolean,
): Promise<BatchProcessAllMetricsResult> => {
  const metricTypes = [
    'primary_metrics',
    'secondary_metrics',
    'guardrail_metrics',
    'control_metrics',
  ] as const;

  // Initialize result with original metrics
  const result: BatchProcessAllMetricsResult = {
    primary_metrics: [...(metricsConfig.primary_metrics || [])],
    secondary_metrics: [...(metricsConfig.secondary_metrics || [])],
    guardrail_metrics: [...(metricsConfig.guardrail_metrics || [])],
    control_metrics: [...(metricsConfig.control_metrics || [])],
    anyChanges: false,
  };

  if (!constraints.length) {
    return result;
  }

  // Collect all metrics that need processing across all types
  const metricsToProcess: Array<{
    metricType: (typeof metricTypes)[number];
    index: number;
    metric: MetricWithDimensionFields;
    needsRepopulation: boolean;
  }> = [];

  for (const metricType of metricTypes) {
    const metrics = metricsConfig[metricType] || [];
    metrics.forEach((metric, index) => {
      if (!metric.name) return;

      // Check if metric needs repopulation due to constraint changes
      if (constraintsChanged && wasPopulationFiltersProcessed(metric)) {
        metricsToProcess.push({
          metricType,
          index,
          metric: cleanMetricForRepopulation(metric),
          needsRepopulation: true,
        });
        return;
      }

      // Check if metric needs initial population
      if (needsPopulationFilters(metric)) {
        metricsToProcess.push({
          metricType,
          index,
          metric,
          needsRepopulation: false,
        });
      }
    });
  }

  // If no metrics need processing, return early
  if (metricsToProcess.length === 0) {
    return result;
  }

  // Make single batch API call for ALL metrics across ALL types
  try {
    const metricNames = metricsToProcess.map((m) => m.metric.name as string);
    const response: PrepopulateFiltersResponse =
      await Service.planner.prepopulateDimensionFilters(
        metricNames,
        constraints,
      );

    // Map batch results back to individual metrics
    for (const item of metricsToProcess) {
      const apiResult = (response?.results ?? []).find(
        (r) => r.metric === item.metric.name,
      );

      if (!apiResult) {
        if (item.needsRepopulation) {
          result[item.metricType][item.index] = {
            ...item.metric,
            populationDerivedFilters: [],
            prepopulationWarnings: [],
          };
          result.anyChanges = true;
        }
        continue;
      }

      // Extract population derived filter metadata
      const populationDerivedFilters = extractPopulationDerivedFilters(
        apiResult.dimension_filters?.[0]?.dimension_filter_constraints || [],
      );

      // Merge prepopulated filters with existing filters
      const existingConstraints =
        item.metric.dimension_filters?.[0]?.dimension_filter_constraints || [];
      const prepopulatedConstraints =
        apiResult.dimension_filters?.[0]?.dimension_filter_constraints || [];

      const mergedConstraints = mergeConstraints(
        existingConstraints,
        prepopulatedConstraints,
      );

      const updatedMetric: MetricWithDimensionFields = {
        ...item.metric,
        dimension_filters:
          mergedConstraints.length > 0
            ? [{ dimension_filter_constraints: mergedConstraints }]
            : item.metric.dimension_filters,
        populationDerivedFilters,
        prepopulationWarnings: apiResult.warnings as PrepopulateWarning[],
      };

      result[item.metricType][item.index] = updatedMetric;

      const hasChanges =
        populationDerivedFilters.length > 0 ||
        (apiResult.warnings?.length ?? 0) > 0 ||
        item.needsRepopulation;

      if (hasChanges) {
        result.anyChanges = true;
      }
    }
  } catch (error) {
    logger.error(
      error,
      'Failed to batch prepopulate dimension filters for all metric types',
    );
    // On error, mark repopulated metrics as processed but with empty results
    for (const item of metricsToProcess) {
      if (item.needsRepopulation) {
        result[item.metricType][item.index] = {
          ...item.metric,
          populationDerivedFilters: [],
          prepopulationWarnings: [],
        };
        result.anyChanges = true;
      }
    }
  }

  return result;
};
